import { useQuery } from "@tanstack/react-query";
import { useParams, useNavigate } from "react-router-dom";
import api, { money } from "@/dealership/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableRow } from "@/components/ui/table";
import { ArrowLeft, TrendingUp, TrendingDown, FileText, Wrench } from "lucide-react";

function Stat({ label, value, accent }) {
  return (
    <Card className="p-5">
      <p className="text-xs uppercase tracking-[0.15em] text-muted-foreground">{label}</p>
      <p className={`font-heading text-2xl font-extrabold mt-2 font-mono-data ${accent || ""}`}>{value}</p>
    </Card>
  );
}

export default function FinancialsVehicleDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data, isLoading } = useQuery({
    queryKey: ["fin-vehicle", id],
    queryFn: async () => (await api.get(`/financials/vehicle/${id}`)).data,
  });

  if (isLoading) return <div className="p-8 text-muted-foreground">Loading…</div>;
  if (!data) return <div className="p-8">Vehicle not found.</div>;

  const { vehicle, cost_items, revenue_items = [], recon_items, total_cost, recon_total, hst_paid, sold, revenue, tax_collected, profit, margin, deal } = data;
  const profitPos = profit >= 0;

  return (
    <div className="p-8 max-w-5xl" data-testid="fin-vehicle-detail-page">
      <button onClick={() => navigate("/dealership/financials")} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-4"><ArrowLeft className="h-4 w-4" />Back to Financials</button>

      <div className="flex items-start justify-between mb-6">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="font-heading text-3xl">{vehicle.label}</h1>
            <Badge className="border-0 bg-secondary text-foreground">{vehicle.status}</Badge>
          </div>
          <p className="text-sm text-muted-foreground mt-1 font-mono-data">Stock {vehicle.stock_number || "—"} · VIN {vehicle.vin || "—"}{vehicle.odometer ? ` · ${vehicle.odometer.toLocaleString()} km` : ""}</p>
        </div>
        <Button variant="outline" onClick={() => navigate(`/dealership/inventory/${id}`)} data-testid="fin-view-inventory"><Wrench className="h-4 w-4 mr-2" />Edit Costs</Button>
      </div>

      <div className="grid sm:grid-cols-4 gap-4 mb-8">
        <Stat label="Total Cost" value={money(total_cost)} />
        <Stat label="Revenue" value={sold ? money(revenue) : "Not sold"} />
        <Stat label="Profit" value={sold ? money(profit) : "—"} accent={sold ? (profitPos ? "text-[#16A34A]" : "text-destructive") : ""} />
        <Stat label="Margin" value={sold ? `${margin}%` : "—"} accent={sold ? (profitPos ? "text-[#16A34A]" : "text-destructive") : ""} />
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <Card className="overflow-hidden">
          <div className="px-5 py-3 border-b border-border flex items-center gap-2 font-heading"><TrendingDown className="h-4 w-4 text-muted-foreground" />Cost Breakdown</div>
          <Table>
            <TableBody>
              {cost_items.map((c) => (
                <TableRow key={c.label}>
                  <TableCell className="text-sm">{c.label}</TableCell>
                  <TableCell className="text-right font-mono-data">{money(c.amount)}</TableCell>
                </TableRow>
              ))}
              <TableRow className="bg-secondary/40">
                <TableCell className="font-semibold">Total Landed Cost</TableCell>
                <TableCell className="text-right font-mono-data font-bold" data-testid="fin-total-cost">{money(total_cost)}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
          {recon_items.length > 0 && (
            <div className="px-5 py-3 border-t border-border">
              <p className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Reconditioning Items ({money(recon_total)})</p>
              <ul className="space-y-1 text-sm" data-testid="fin-recon-items">
                {recon_items.map((r, i) => (
                  <li key={i} className="flex justify-between"><span className="text-muted-foreground flex items-center gap-2">{r.description || "Item"}{r.kind === "mechanic" ? <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-amber-500/12 text-amber-400">Mechanic{r.status ? ` · ${r.status}` : ""}</span> : null}</span><span className="font-mono-data">{money(r.cost)}</span></li>
                ))}
              </ul>
            </div>
          )}
        </Card>

        <Card className="overflow-hidden">
          <div className="px-5 py-3 border-b border-border flex items-center gap-2 font-heading"><TrendingUp className="h-4 w-4 text-muted-foreground" />Revenue & Sale</div>
          <Table>
            <TableBody>
              {revenue_items.length > 0 ? revenue_items.map((r, i) => (
                <TableRow key={i}><TableCell className="text-sm">{r.label}</TableCell><TableCell className="text-right font-mono-data">{money(r.amount)}</TableCell></TableRow>
              )) : (
                <TableRow><TableCell className="text-sm">{sold ? "Sale Price" : "Listing Price"}</TableCell><TableCell className="text-right font-mono-data">{money(revenue)}</TableCell></TableRow>
              )}
              {revenue_items.length > 1 && (
                <TableRow className="bg-secondary/20"><TableCell className="font-medium">Total Revenue</TableCell><TableCell className="text-right font-mono-data font-semibold" data-testid="fin-total-revenue">{money(revenue)}</TableCell></TableRow>
              )}
              <TableRow><TableCell className="text-sm">HST Collected</TableCell><TableCell className="text-right font-mono-data text-muted-foreground">{money(tax_collected)}</TableCell></TableRow>
              <TableRow><TableCell className="text-sm">HST Paid on Purchase (ITC)</TableCell><TableCell className="text-right font-mono-data text-muted-foreground">{money(hst_paid)}</TableCell></TableRow>
              <TableRow className="bg-secondary/40">
                <TableCell className="font-semibold">Gross Profit</TableCell>
                <TableCell className={`text-right font-mono-data font-bold ${profitPos ? "text-[#16A34A]" : "text-destructive"}`} data-testid="fin-profit">{sold ? money(profit) : "—"}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
          {deal && (
            <div className="px-5 py-3 border-t border-border flex items-center justify-between">
              <div>
                <p className="text-sm font-medium">{deal.customer_name || "Customer"}</p>
                <p className="text-xs text-muted-foreground font-mono-data">Bill #{deal.bill_number} · {deal.deal_date}</p>
              </div>
              <Button variant="outline" size="sm" onClick={() => navigate(`/dealership/bill/${deal.id}`)} data-testid="fin-view-bill"><FileText className="h-4 w-4 mr-2" />Bill of Sale</Button>
            </div>
          )}
          {!sold && <p className="px-5 py-3 text-xs text-muted-foreground border-t border-border">This unit hasn't been sold yet — revenue shown is the current listing price.</p>}
        </Card>
      </div>
    </div>
  );
}
